import { useState, useRef } from "react";
import { useTranslate } from "@tolgee/react";
import { useNotify } from "../Helpers/Notifications";
import {
  deepClone,
  isPollModified as checkPollModified,
  createEmptyPoll,
  createEmptyOption,
  isTempPoll,
  isTempOption,
  validatePoll as validatePollHelper,
} from "../Helpers/pollHelpers";

export const usePollManagement = ({
  conferenceId = null,
  initialPolls = [],
  isEditMode = false,
  MAX_POLLS,
  MAX_OPTIONS,
  MIN_OPTIONS,
  QUESTION_MIN_LENGTH,
  QUESTION_MAX_LENGTH,
  OPTION_MAX_LENGTH,
  conferencesService,
}) => {
  const { t } = useTranslate();
  const { notifyError, notifySuccess } = useNotify();
  
  const [polls, setPolls] = useState(initialPolls);
  const [originalPolls, setOriginalPolls] = useState(deepClone(initialPolls));
  const [expandedPollId, setExpandedPollId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [savingPollId, setSavingPollId] = useState(null);
  
  const questionInputRef = useRef(null);
  const optionInputRefs = useRef({});

  const initializePolls = (loadedPolls) => {
    setPolls(loadedPolls);
    setOriginalPolls(deepClone(loadedPolls));
    if (loadedPolls.length > 0) {
      const active = loadedPolls.find(p => p.status === "launched");
      setExpandedPollId(active ? active.id : loadedPolls[0].id);
    }
  };

  const isPollModified = (pollId) => {
    const currentPoll = polls.find(p => p.id === pollId);
    const originalPoll = originalPolls.find(p => p.id === pollId);
    return checkPollModified(currentPoll, originalPoll, QUESTION_MIN_LENGTH, MIN_OPTIONS);
  };

  const getModifiedPolls = () => {
    return polls.filter(poll => isPollModified(poll.id));
  };

  const handleAddPoll = () => {
    if (polls.length >= MAX_POLLS) {
      notifyError(t("conferences.max_polls_reached", { max: MAX_POLLS }));
      return;
    }

    const newPoll = createEmptyPoll();
    setPolls([...polls, newPoll]);
    setExpandedPollId(newPoll.id);
    setFieldErrors({});
    
    setTimeout(() => {
      questionInputRef.current?.focus();
    }, 100);
  };

  const handleUpdateQuestionText = (pollId, text) => {
    setPolls((prev) =>
      prev.map((poll) =>
        poll.id === pollId ? { ...poll, question_text: text } : poll
      )
    );
    
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors[pollId]) {
        delete newErrors[pollId].question;
        if (Object.keys(newErrors[pollId]).length === 0) {
          delete newErrors[pollId];
        }
      }
      return newErrors;
    });
  };

  const handleUpdateAnswerType = (pollId, type) => {
    setPolls((prev) =>
      prev.map((poll) =>
        poll.id === pollId ? { ...poll, answer_type: type } : poll
      )
    );
  };

  const handleAddOption = (pollId) => {
    setPolls((prev) =>
      prev.map((poll) => {
        if (poll.id !== pollId) return poll;
        if (poll.options.length >= MAX_OPTIONS) {
          notifyError(t("conferences.max_options_reached", { max: MAX_OPTIONS }));
          return poll;
        }
        const newOption = {
          ...createEmptyOption(),
          position: poll.options.length,
        };
        
        setTimeout(() => {
          optionInputRefs.current[newOption.id]?.focus();
        }, 100);
        
        return { ...poll, options: [...poll.options, newOption] };
      })
    );
  };


  const handleUpdateOptionText = (pollId, optionId, text) => {
    setPolls((prev) =>
      prev.map((poll) => {
        if (poll.id !== pollId) return poll;
        return {
          ...poll,
          options: poll.options.map((opt) =>
            opt.id === optionId ? { ...opt, option_text: text } : opt
          ),
        };
      })
    );
    
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors[pollId]?.[optionId]) {
        delete newErrors[pollId][optionId];
        if (Object.keys(newErrors[pollId]).length === 0) {
          delete newErrors[pollId];
        }
      }
      return newErrors;
    });
  };

  const handleDeleteOption = (pollId, optionId) => {
    const poll = polls.find((p) => p.id === pollId);
    if (!poll) return;

    if (poll.options.length <= MIN_OPTIONS) {
      notifyError(t("conferences.min_options_required", { min: MIN_OPTIONS }));
      return;
    }

    setPolls((prev) =>
      prev.map((p) => {
        if (p.id !== pollId) return p;
        return { 
          ...p, 
          options: p.options.filter((opt) => opt.id !== optionId) 
        };
      })
    );

    if (isEditMode && !isTempOption(optionId) && conferencesService?.deletePollOption) {
      conferencesService.deletePollOption(
        optionId,
        () => {
          notifySuccess(t("conferences.option_deleted"));
          setOriginalPolls((prev) =>
            prev.map((p) => {
              if (p.id !== pollId) return p;
              return {
                ...p,
                options: p.options.filter((opt) => opt.id !== optionId),
              };
            })
          );
        },
        (error) => {
          notifyError(t("general.something_wrong"));
        }
      );
    } else if (!isEditMode || isTempOption(optionId)) {
      setOriginalPolls((prev) =>
        prev.map((p) => {
          if (p.id !== pollId) return p;
          return {
            ...p,
            options: p.options.filter((opt) => opt.id !== optionId),
          };
        })
      );
    }
  };


  const validatePoll = (poll) => {
    const errors = validatePollHelper(poll, QUESTION_MIN_LENGTH, MIN_OPTIONS, t);
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, [poll.id]: errors }));
      return false;
    }

    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[poll.id];
      return newErrors;
    });
    return true;
  };

  const handleDeletePoll = (pollId, onSuccess) => {
    const poll = polls.find((p) => p.id === pollId);
    if (!poll) return;

    if (poll.status === "launched" || poll.status === "ended") {
      notifyError(t("conferences.cannot_delete_active_poll"));
      return;
    }

    if (isEditMode && !isTempPoll(pollId) && conferencesService?.deletePollQuestion) {
      conferencesService.deletePollQuestion(
        pollId,
        () => {
          notifySuccess(t("conferences.poll_deleted"));
          setPolls((prev) => prev.filter((p) => p.id !== pollId));
          setOriginalPolls((prev) => prev.filter((p) => p.id !== pollId));
          if (onSuccess) onSuccess();
        },
        (error) => {
          notifyError(t("general.something_wrong"));
        }
      );
    } else {
      setPolls((prev) => prev.filter((p) => p.id !== pollId));
      setOriginalPolls((prev) => prev.filter((p) => p.id !== pollId));
      notifySuccess(t("conferences.poll_deleted"));
    
      if (onSuccess) onSuccess();
    }
  };

  const handleToggleExpand = (pollId) => {
    setExpandedPollId(expandedPollId === pollId ? null : pollId);
  };

  const handleReorderPolls = (newPolls, saveCallback) => {
    setPolls(newPolls);
    
    if (!isEditMode || !conferencesService?.reorderPollQuestions) return;
    
    const positions = {};
    newPolls.forEach((poll, index) => {
      if (!isTempPoll(poll.id)) {
        positions[poll.id] = index;
      }
    });
    
    if (Object.keys(positions).length === 0) return;
    
    conferencesService.reorderPollQuestions(
      conferenceId,
      positions,
      () => {
        setOriginalPolls(deepClone(newPolls));
        if (saveCallback) saveCallback();
      },
      (error) => {
        console.error('Failed to reorder polls:', error);
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const handleReorderOptions = (pollId, newOptions) => {
    setPolls((prev) =>
      prev.map((poll) => (poll.id === pollId ? { ...poll, options: newOptions } : poll))
    );
    
    if (!isEditMode || isTempPoll(pollId) || !conferencesService?.reorderPollOptions) return;
    
    const positions = {};
    newOptions.forEach((option, index) => {
      if (!isTempOption(option.id)) {
        positions[option.id] = index;
      }
    });
    
    if (Object.keys(positions).length === 0) return;
    
    conferencesService.reorderPollOptions(
      pollId,
      positions,
      () => {
        setOriginalPolls(prev => 
          prev.map(poll => 
            poll.id === pollId 
              ? { ...poll, options: deepClone(newOptions) }
              : poll
          )
        );
      },
      (error) => {
        notifyError(t("general.something_wrong"));
      }
    );
  };

  return {
    polls,
    setPolls,
    originalPolls,
    setOriginalPolls,
    expandedPollId,
    setExpandedPollId,
    fieldErrors,
    setFieldErrors,
    savingPollId,
    setSavingPollId,
    
    questionInputRef,
    optionInputRefs,
    
    initializePolls,
    isPollModified,
    getModifiedPolls,
    handleAddPoll,
    handleUpdateQuestionText,
    handleUpdateAnswerType,
    handleAddOption,
    handleUpdateOptionText,
    handleDeleteOption,
    validatePoll,
    handleDeletePoll,
    handleToggleExpand,
    handleReorderPolls,
    handleReorderOptions,
  };
};